'use client'
import type { GenericErrorResponse, TeamResponse } from 'src/api/responses'

import { useCache } from '@effektify/cache'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

import { CURRENT_TEAM_KEY } from 'src/constants/session'
import { QUERY_KEYS } from 'src/constants/queries'
import { Teams } from 'src/api/Teams'

import useApi from './useApi'
import useAppStorage from './useAppStorage'
import useAuth from './useAuth'

const isTeamListResponse = (data: unknown): data is TeamResponse[] => {
  if (!Array.isArray(data)) return false
  if (data.length === 0) return true // Empty array is valid
  if (typeof data[0] !== 'object' || data[0] === null) return false
  if (!('id' in data[0]) || typeof data[0].id !== 'string') return false
  if (!('name' in data[0]) || typeof data[0].name !== 'string') return false
  return true
}

const isTeamResponse = (data: unknown): data is TeamResponse => {
  if (!data || typeof data !== 'object' || data === null || Object.keys(data).length === 0) return true // Empty array is valid
  if (!('id' in data) || typeof data.id !== 'string') return false
  if (!('name' in data) || typeof data.name !== 'string') return false
  return true
}

const useTeams = () => {
  const { resetCacheData } = useCache()

  const {
    setItem,
    // getItem
  } = useAppStorage()
  const { isAuthenticated, logout, handleGlobalAuthError } = useAuth()
  const { companyId = '' } = useParams<{ companyId: string }>()

  // const storedTeam = JSON.parse(getItem(CURRENT_TEAM_KEY) || 'null') as TeamResponse | null;
  const teamClient = useApi(Teams)
  const [teamId, setTeamId] = useState<string>(companyId)
  const [teams, setTeams] = useState<TeamResponse[] | null>(null)
  // Derived, not stored: nothing else ever set this.
  const isInitialized = teams !== null
  const [currentTeam, setCurrentTeam] = useState<TeamResponse | null>(null)

  const { data: teamsData, isLoading: teamsLoading, refetch: fetchTeamList } = useQuery({
    queryKey: [QUERY_KEYS.TEAMS.LIST],
    queryFn: async () => {
      try {
        const response = await teamClient.getTeams()
        const { data } = response
        if (!isTeamListResponse(data)) {
          throw new Error('Invalid team response format')
        }
        return data
      }
      catch (e: unknown) {
        if (!e || typeof e !== 'object' || !('error' in e)) {
          throw e
        }
        const error = e.error as GenericErrorResponse
        if (error.error?.status === '401 Unauthorized' || error.error?.status === '403 Forbidden') {
          await logout()
          handleGlobalAuthError()
          throw new Error('Not authenticated')
        }
      }
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 15, // 15 minutes
  })

  const { data: teamData, isLoading: teamLoading, refetch: fetchTeam } = useQuery({
    queryKey: [QUERY_KEYS.TEAMS.GET, teamId],
    queryFn: async () => {
      try {
        const response = await teamClient.getTeam(teamId)
        const { data } = response
        if (!isTeamResponse(data)) {
          throw new Error('Invalid team response format')
        }
        return data
      }
      catch (e: unknown) {
        if (!e || typeof e !== 'object' || !('error' in e)) {
          throw e
        }
        const error = e.error as GenericErrorResponse
        if (error?.error?.status === '401 Unauthorized' || error?.error?.status === '403 Forbidden') {
          await logout()
          handleGlobalAuthError()
          throw new Error('Not authenticated')
        }
        else {
          throw e
        }
      }
    },
    enabled: !!teamId && isAuthenticated,
    staleTime: 1000 * 60 * 15, // 15 minutes
  })

  const clearCurrentTeam = useCallback(() => {
    setCurrentTeam(null)
    setItem(CURRENT_TEAM_KEY, 'null')
  }, [setItem, setCurrentTeam])

  const flushTeamCache = useCallback(async () => {
    setTeams(null)
    clearCurrentTeam()
    await resetCacheData([QUERY_KEYS.TEAMS.LIST])
    await resetCacheData([QUERY_KEYS.TEAMS.GET, teamId])
    await resetCacheData([QUERY_KEYS.AUTH.AD_STUDIO])
  }, [resetCacheData, teamId, clearCurrentTeam])

  const selectTeam = useCallback(async (newTeamId: string) => {
    if (newTeamId) {
      await resetCacheData([QUERY_KEYS.AUTH.AD_STUDIO])
      setTeamId(newTeamId)
    }
  }, [resetCacheData, setTeamId])

  useEffect(() => {
    if (teamsData) {
      // Looks like a pure mirror but is not: `flushTeamCache` also clears `teams` imperatively, so
      // the value is query-data-or-locally-reset. Returning `teamsData` directly would silently
      // undo that reset on the next render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTeams(teamsData)
    }
  }, [teamsData, setTeams])

  useEffect(() => {
    if (teamData) {
      let combinedTeamData = teamData
      if (teams) {
        const teamFromList = teams.find(t => t.id === teamData.id)
        if (teamFromList) {
          combinedTeamData = { ...teamFromList, ...teamData }
        }
      }
      // Merges the team query with its entry from the list and writes localStorage — not a pure
      // mirror, so it cannot simply return the query value.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentTeam(combinedTeamData)
      setItem(CURRENT_TEAM_KEY, combinedTeamData)
    }
  }, [teamData, setCurrentTeam, teams, setItem])

  // useEffect(() => {
  //   if (teamIdParam && teams.length > 0) {
  //     const team = teams.find(t => t.id === teamIdParam);
  //     if (team) {
  //       setCurrentTeam(team);
  //       setItem(CURRENT_TEAM_KEY, JSON.stringify(team));
  //     } else {
  //       console.warn(`Team with ID ${teamIdParam} not found in the list of teams.`);
  //     }
  //   }
  // }, [teamIdParam, teams, setItem]);

  return {
    currentTeam: currentTeam || teamData || null,
    teams,
    isLoading: !isInitialized || teamsLoading || teamLoading,
    flushTeamCache,
    selectTeam,
    clearCurrentTeam,
    fetchTeamList,
    fetchTeam,
  }
}

export default useTeams
